// The finished page with the certificate (Certificate.dc.html), at <product path>/certificate, where
// the bell's "Your certificate for The Funding Engine is ready" opens. It reads their own certificate
// (hub.certificates, not revoked) and the product's settings.certificate (issuer, intake_line):
// - with an intake: "Cohort 3, 9 September to 14 October" and "has completed The Funding Engine:
//   6 live weeks with Adam Slee, from 9 September to 14 October 2026.";
// - without one (Adam, 3 Oct): "has completed The Funding Engine.", dated the day the rule was met.
// Download the PDF (certificate-pdf), Add it to LinkedIn (LinkedIn's own add-a-certification page,
// with the name, issuer, date, number and check link filled in) and Copy the check link, which
// carries the certificate's random token, so the number alone shows nobody anything (Adam, 3 Oct).
// No board draws it on a phone: there it stacks, and the certificate keeps its shape.
import { useState } from 'react'
import { copyText } from '../lib/clipboard.js'
import { HubFunctionError } from '../lib/functions.js'
import { useViewingAs, VIEWING_AS_LINE } from '../lib/viewAs.jsx'
import { SITE } from '../config/site.js'
import { useProductData } from './ProductData.jsx'
import { certificatePdf } from './data.js'
import { certificateDate, completedLine, linkedinUrl, notReadyLine, periodLine, readyLine, staysLine } from './certificate.js'
import { DownloadLine } from './Icons.jsx'
import '../styles/certificate.css'

const FALLBACK = 'Something went wrong. Try again.'

export function CertificatePage({ product }) {
  const { data } = useProductData()
  const viewingAs = useViewingAs()
  const [pdf, setPdf] = useState({ status: 'idle', line: null })
  const [li, setLi] = useState(false)
  const [copied, setCopied] = useState({ done: false, line: null })
  const p = data.product || product
  const c = data.certificate
  const settings = p.settings?.certificate || {}
  const issuer = settings.issuer || SITE.company

  if (!c) {
    return (
      <section className="ct-none">
        <h1 className="ct-h1">Your certificate isn't ready yet</h1>
        <p className="ct-lede">{notReadyLine(p)}</p>
      </section>
    )
  }

  const checkUrl = `${window.location.origin}${c.check_path}`
  const checkShown = `${window.location.host}${c.check_path}`
  const eyebrow = c.cohort_label ? [c.cohort_label, periodLine(c, false)].filter(Boolean).join(', ') : null

  async function download() {
    if (pdf.status === 'busy') return
    if (viewingAs) { setPdf({ status: 'failed', line: VIEWING_AS_LINE }); return }
    setPdf({ status: 'busy', line: null })
    try {
      const blob = await certificatePdf(c.id)
      if (!(blob instanceof Blob)) throw new Error('no pdf')
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = `${c.course_name} certificate ${c.number}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(href), 1000)
      setPdf({ status: 'done', line: null })
    } catch (e) {
      setPdf({ status: 'failed', line: e instanceof HubFunctionError ? e.message : FALLBACK })
    }
  }

  async function copy() {
    const ok = await copyText(checkUrl)
    setCopied(ok ? { done: true, line: null } : { done: false, line: `The link couldn't be copied. Here it is to copy yourself: ${checkShown}` })
  }

  return (
    <div className="ct">
      <section className="ct-head">
        {eyebrow && <div className="ct-eyebrow">{eyebrow}</div>}
        <h1 className="ct-h1">You've finished {c.course_name}</h1>
        <p className="ct-lede">{readyLine(p.certificate_rule, p.type)}</p>
      </section>

      <div className="ct-frame">
        <section className="ct-paper" aria-label="Your certificate">
          <span className="ct-edge" aria-hidden="true" />
          <div className="ct-top">
            <div className="ct-brand">
              <span className="ct-course">{c.course_name}</span>
              <span className="ct-rule" aria-hidden="true" />
            </div>
            <span className="ct-number">Certificate {c.number}</span>
          </div>
          <div className="ct-middle">
            <span className="ct-kind">Certificate of completion</span>
            <span className="ct-name">{c.name_on_certificate}</span>
            <span className="ct-line">{completedLine(c, settings.intake_line)}</span>
          </div>
          <div className="ct-bottom">
            <div className="ct-sign">
              <span className="ct-signature">Adam Slee</span>
              <span className="ct-sign-rule" aria-hidden="true" />
              <span className="ct-sign-name">Adam Slee</span>
            </div>
            <div className="ct-date">
              <span className="ct-date-day">{certificateDate(c.completed_on)}</span>
              <span className="ct-check">Check it at {checkShown}</span>
            </div>
          </div>
        </section>
      </div>

      <section className="ct-actions">
        <span className="ct-act">
          <button type="button" className="ct-btn ct-btn--main" onClick={download} aria-busy={pdf.status === 'busy' || undefined}>
            <DownloadLine />{pdf.status === 'done' ? 'PDF downloaded' : 'Download the PDF'}
          </button>
        </span>
        <a className="ct-btn" href={linkedinUrl(c, issuer, checkUrl)} target="_blank" rel="noopener noreferrer" onClick={() => setLi(true)}>
          {li ? 'Opened LinkedIn, with the details filled in' : 'Add it to LinkedIn'}
        </a>
        <button type="button" className="ct-btn" onClick={copy}>{copied.done ? 'Check link copied' : 'Copy the check link'}</button>
      </section>
      {(pdf.line || copied.line) && <p className="ct-msg" role="alert">{pdf.line || copied.line}</p>}

      <section className="ct-notes">
        <div className="ct-note">
          <h2 className="ct-h2">What the check link shows</h2>
          <p>Anyone with the link sees your name, the course, {c.cohort_label ? 'the cohort, ' : ''}the date you finished and the certificate number, issued by {issuer}. Nothing else about you is shown.</p>
        </div>
        <div className="ct-note ct-note--filled">
          <h2 className="ct-h2">Everything stays here</h2>
          <p>{staysLine(p)}</p>
        </div>
      </section>
    </div>
  )
}
